"use client"

import { useEffect, useState } from "react"
import { useTranslations } from "next-intl"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { useAuthStore } from "@/stores/auth-store"
import {
  connectTelephonySocket,
  isTelephonySocketConnected,
  onTelephonyConnect,
  onAgentStatusChanged,
  onAgentStatuses,
  emitSetAgentStatus,
  requestAgentStatuses,
} from "@/lib/telephony-socket"
import { getMyAvailability, updateMyAvailability } from "@/lib/agent-availability-api"
import type { AgentStatus } from "@/types"

const STATUS_COLOR: Record<AgentStatus, string> = {
  available: "bg-green-500",
  "on-call": "bg-yellow-500",
  dnd: "bg-red-500",
  "pausa-pranzo": "bg-red-500",
  "pausa-call": "bg-red-500",
  "wrap-up": "bg-orange-500",
  offline: "bg-gray-400",
}

const SELECTABLE_STATUSES: AgentStatus[] = [
  "available",
  "dnd",
  "pausa-pranzo",
  "pausa-call",
  "offline",
]

export function AgentStatusSelector() {
  const t = useTranslations("telephony")
  const { user } = useAuthStore()
  const { state, isMobile } = useSidebar()
  const [status, setStatus] = useState<AgentStatus>("offline")

  const STATUS_LABEL: Record<AgentStatus, string> = {
    available: t("agentStatus.available"),
    "on-call": t("agentStatus.onCall"),
    dnd: t("agentStatus.doNotDisturb"),
    "pausa-pranzo": t("agentStatus.pausaPranzo"),
    "pausa-call": t("agentStatus.pausaCall"),
    "wrap-up": t("agentStatus.wrapUp"),
    offline: t("agentStatus.offline"),
  }

  const extension = user?.sip_extension

  useEffect(() => {
    if (!extension) return

    // Fetch initial status from API
    getMyAvailability()
      .then((data) => {
        setStatus(data.status)
      })
      .catch(() => {
        // Silently ignore — default to offline
      })

    connectTelephonySocket()

    const cleanupConnect = onTelephonyConnect(() => {
      requestAgentStatuses()
    })

    const cleanupStatuses = onAgentStatuses((entries) => {
      const mine = entries.find((e) => e.extension === extension)
      if (mine) {
        setStatus(mine.status)
      }
    })

    const cleanupChanged = onAgentStatusChanged((data) => {
      if (data.extension === extension) {
        setStatus(data.status)
      }
    })

    // Every page renders its own sidebar, so this selector remounts on each
    // navigation while the shared socket stays connected: no "connect" event
    // fires again, and agents without calls.view get a 403 from the API above.
    // Without this the pill fell back to "Offline" after every page change.
    if (isTelephonySocketConnected()) {
      requestAgentStatuses()
    }

    return () => {
      cleanupConnect()
      cleanupStatuses()
      cleanupChanged()
    }
  }, [extension])

  if (!extension) return null

  const currentColor = STATUS_COLOR[status]
  const currentLabel = STATUS_LABEL[status]

  const handleSelect = async (newStatus: AgentStatus) => {
    if (newStatus === status) return
    setStatus(newStatus)
    // Update via socket (real-time) and API (persistence)
    emitSetAgentStatus(extension, newStatus)
    try {
      await updateMyAvailability(newStatus)
    } catch {
      // Silently ignore API error — socket update still went through
    }
  }

  // Icon-collapsed desktop rail → dot-only trigger with a right-side flyout;
  // expanded (or mobile sheet) → full row with a top-side menu (NavUser pattern).
  const collapsed = state === "collapsed" && !isMobile

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton tooltip={currentLabel} aria-label={currentLabel}>
              <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${currentColor}`} />
              <span className="text-sm group-data-[collapsible=icon]:hidden">{currentLabel}</span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-44"
            side={collapsed ? "right" : "top"}
            align={collapsed ? "end" : "start"}
          >
            {SELECTABLE_STATUSES.map((s) => {
              return (
                <DropdownMenuItem
                  key={s}
                  onClick={() => void handleSelect(s)}
                  className="flex items-center gap-2"
                >
                  <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${STATUS_COLOR[s]}`} />
                  {STATUS_LABEL[s]}
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
